/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    Component,
    Input,
    Output,
    EventEmitter,
    Injector,
    NgZone,
    Optional,
    ElementRef,
    QueryList,
    ViewChildren,
    AfterViewInit,
    OnInit
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { QRCodeComponent } from 'angularx-qrcode';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { BackToCloseModalDirective } from '../../directives/back-to-close-modal.directive';
import { FastOneDimensionSpatialNavigationDirective } from '../../directives/fast-spatial-navigation/fast-one-dimenstion-spatial-navigation.directive';
import { FastSpatialNavigationItemDirective } from '../../directives/fast-spatial-navigation/fast-spatial-navigation-item.directive';
import { NvHigBackgroundImageOverlayDirective } from '../../directives/nv-hig-background-image-overlay.directive';
import { NvAddKeyboardActivateDirective } from '../../directives/nv-mat-keyboard-fixups.directive';
import { CheckboxFocusMonitorDirective } from '../../directives/nv-mat-spatial-navigation-fixups.directive';
import { TrapFocusDirective } from '../../directives/trap-focus.directive';
import { NvDialogButton, NvHigTableColumns } from '../../models/hig-modal-shared.types';
import { FocusManager } from '../../services/focus-manager';
import { NvHigTableComparisonComponent } from '../nv-hig-generic-table/nv-hig-table_comparison.component';
import { NvHigListMetadataComponent } from '../nv-hig-list-metadata/nv-hig-list-metadata.component';
import { NvHigFullPageDialogData, NvHigChoiceChipItem, NvHigSelectorCardsData } from './nv-hig-fullpage-dialog.types';

@Component({
    selector: 'nv-hig-fullpage-dialog',
    templateUrl: './nv-hig-fullpage-dialog.component.html',
    styleUrls: ['./nv-hig-fullpage-dialog.component.scss'],
    imports: [
        NvHigBackgroundImageOverlayDirective,
        FlexModule,
        TrapFocusDirective,
        QRCodeComponent,
        MatIconModule,
        AutoFocusDirective,
        MatButtonModule,
        FastOneDimensionSpatialNavigationDirective,
        FastSpatialNavigationItemDirective,
        NgStyle,
        ExtendedModule,
        TranslateModule,
        NvHigTableComparisonComponent,
        NvHigListMetadataComponent,
        MatCheckboxModule,
        FormsModule,
        CheckboxFocusMonitorDirective,
        NvAddKeyboardActivateDirective
    ]
})
/**
 *
 * This component is responsible for rendering a full-page dialog based on the provided data,
 * including setting a background image, managing translucent sheet, and handling button click events.
 * It also determines whether to render a Selector Card or Choice Chip layout based on the content in NvHigFullPageDialogData.
 * This component extends BackToCloseModalDirective to manage ESC/Back to close the dialog.
 *
 * @FutureWork
 * Extract out SelectorCard, ChoiceChip and Step into HIG components.
 */
export class NvHigFullPageDialogComponent<T> extends BackToCloseModalDirective implements AfterViewInit, OnInit {
    @Input() data: NvHigFullPageDialogData<T>;
    @Output() buttonClick = new EventEmitter<NvDialogButton<T>>();
    @Output() tableCTAClick = new EventEmitter<{ column: NvHigTableColumns }>();
    @Output() closeButtonClick = new EventEmitter<void>();

    readonly qrCodeWidth = 200;
    isRTL = false;
    expandedSelectorCardIndex = 0;
    choiceChip: NvHigChoiceChipItem<T>;
    selectorCards: NvHigSelectorCardsData<T>;

    @ViewChildren('selectorCardElements') selectorCardElements: QueryList<ElementRef<HTMLElement>>;

    expandSelectorCard(index: number, skipFocusIfCardExpanded: boolean = true): void {
        if (
            skipFocusIfCardExpanded &&
            index === this.expandedSelectorCardIndex &&
            this.selectorCardElements
                .get(this.expandedSelectorCardIndex)
                ?.nativeElement?.contains(document.activeElement)
        ) {
            return;
        }
        this.expandedSelectorCardIndex = index;
        setTimeout(() => {
            this.focusManager.focusChild(
                this.selectorCardElements.get(this.expandedSelectorCardIndex).nativeElement,
                'initial'
            );
        });
    }

    constructor(
        injector: Injector,
        @Optional() dialogRef: MatDialogRef<any>,
        @Optional() bottomSheetRef: MatBottomSheetRef<any>,
        ngZone: NgZone,
        private focusManager: FocusManager,
        private localeService: LocaleService
    ) {
        super(dialogRef, bottomSheetRef, injector, ngZone);
        this.data = injector.get(MAT_DIALOG_DATA, null);
        this.isRTL = this.localeService.isRTLLayoutEnabled;
        if (this.data.content) {
            this.setContentData(this.data.content);
        }
    }

    onButtonClick(button: NvDialogButton<T>) {
        this.buttonClick.emit(button);
    }

    onCloseClick() {
        this.closeButtonClick.emit();
    }

    /**
     * Sets content data for the component, to workaround template errors in Angular.
     *
     * This component has multiple types and the template struggles to understand and downcast the data correctly.
     * This method manually checks if the provided content is of type `NvHigChoiceChipItem` or `NvHigSelectorCardsData`,
     * assigning it to the appropriate property (`choiceChip` or `selectorCards`).
     *
     */
    private setContentData(content: NvHigChoiceChipItem<T> | NvHigSelectorCardsData<T>) {
        if ((content as NvHigChoiceChipItem<T>).choiceChipTitle) {
            this.choiceChip = content as NvHigChoiceChipItem<T>;
        } else {
            this.selectorCards = content as NvHigSelectorCardsData<T>;
        }
    }

    ngAfterViewInit(): void {
        if (this.selectorCardElements.length > 0) {
            this.expandSelectorCard(this.expandedSelectorCardIndex, false);
        }
    }

    ngOnInit(): void {
        this.data.checkboxState = this.data.checkbox?.initialState ?? false;
    }

    onTableCTAClick(event: { column: NvHigTableColumns; columnIndex: number }): void {
        this.tableCTAClick.emit(event);
    }
}
